import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Container, Button, Stack } from '@mui/material';
import kpcLogo from '@/assets/icons/kpc-logo-v2.svg';
import textIcon from '@/assets/icons/text.svg';
import imageIcon from '@/assets/icons/image.svg';

import TextGenerationTab from '@/components/ExamPage/TextGenerationTab';
import ImageGenerationTab from '@/components/ExamPage/ImageGenerationTab';
import { userService } from '@/services/userService';
import { configService } from '@/services/configService';

// 공통 상태 타입 정의 / Common state type definition
interface SharedTabState {
  // 텍스트 생성 탭 상태 / Text generation tab state
  textPrompt: string;
  textTranslatedPrompt: string;
  textMessages: any[];
  textShowTranslation: boolean;

  // 이미지 생성 탭 상태 / Image generation tab state
  imagePrompt: string;
  imageMessages: any[];
  imageCopiedPrompts: any[];
  imageShowCopiedPrompts: boolean;
}

// 탭 패널 제거하고 URL 파라미터로 분기 / Use URL params instead of TabPanel

const ExamPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [type, setType] = useState<'text' | 'image'>(() =>
    (searchParams.get('type') as 'text' | 'image') || 'text'
  );
  // const [showSettingsModal, setShowSettingsModal] = useState(false);
  // const [showExpandedHistory, setShowExpandedHistory] = useState(false);
  const [userId, setUserId] = useState<string>('');

  // 공통 상태 관리 / Shared state management
  const [sharedState, setSharedState] = useState<SharedTabState>({
    // 텍스트 생성 탭 상태 / Text generation tab state
    textPrompt: '',
    textTranslatedPrompt: '',
    textMessages: [],
    textShowTranslation: false,

    // 이미지 생성 탭 상태 / Image generation tab state
    imagePrompt: '',
    imageMessages: [],
    imageCopiedPrompts: [],
    imageShowCopiedPrompts: false,
  });

  const switchType = (next: 'text' | 'image') => {
    // URL에는 반영하지 않고 내부 상태로만 탭 전환
    // Switch tabs using local state without reflecting in URL
    setType(next);
  };

  useEffect(() => {
    // 초기 로드 시 URL에서 탭 관련 파라미터 제거하고 user, expKey만 유지
    // On mount, remove tab-related params from URL and keep only 'user', 'expKey'
    const currentParams = new URLSearchParams(searchParams);
    const userParam = currentParams.get('user');
    const expKeyParam = currentParams.get('expKey');
    const hasType = currentParams.has('type');
    // 만약 type이 있거나 user, expKey 외 파라미터가 있으면 정리
    let extraCount = 0;
    currentParams.forEach((_, key) => {
      if (key !== 'user' && key !== 'expKey' && key !== 'type') extraCount += 1;
    });
    if (hasType || extraCount > 0) {
      const nextParams = new URLSearchParams();
      if (userParam) nextParams.set('user', userParam);
      if (expKeyParam) nextParams.set('expKey', expKeyParam);
      setSearchParams(nextParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 사용자 정보 백그라운드 로드
  // Load user information in background
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        // URL에 user 파라미터가 있으면 항상 새로 로드 (캐시 무시)
        // If user parameter exists in URL, always load fresh (ignore cache)
        const urlParams = new URLSearchParams(window.location.search);
        const hasUserParam = urlParams.has('user');
        
        let user = null;
        
        if (hasUserParam) {
          // URL에 user 파라미터가 있으면 새로 로드
          // If user parameter exists, load fresh from URL
          console.log('URL에 user 파라미터가 있습니다. 새로 로드합니다. / User parameter found in URL, loading fresh');
          user = await userService.getUserInfo('user');
          if (user) {
            userService.saveUserToStorage(user);
          }
        } else {
          // URL에 user 파라미터가 없으면 계정연동실패로 처리
          // If no user parameter, treat as account linkage failure
          console.log('URL에 user 파라미터가 없습니다. 계정연동실패로 처리합니다. / No user parameter in URL, treating as account linkage failure');
          user = null;
        }

        // 사용자 ID 설정
        // Set user ID
        if (user && user.id) {
          setUserId(user.id);
          
          // 사용자 정보 로드 후 설정값 로드
          // Load config limits after user info is loaded
          try {
            await configService.loadConfigLimits();
          } catch (error) {
            console.error('설정값 로드 중 오류 발생 / Error loading config limits:', error);
          }
        } else {
          // 사용자 정보가 없거나 ID가 없으면 계정연동실패 표시
          // If user info is missing or ID is missing, show account linkage failure
          setUserId('계정연동실패');
        }
      } catch (error) {
        console.error('사용자 정보 로드 오류 / Error loading user information:', error);
        // 에러 발생 시 계정연동실패 표시
        // Show account linkage failure on error
        setUserId('계정연동실패');
      }
    };

    loadUserInfo();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white' }}>
      {/* 메인 컨텐츠 영역 / Main Content Area */}
      <Container maxWidth="xl" sx={{ py: 0 }}>
        {/* 상단 탭바 / Top Tab Bar */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ py: 2, borderColor: 'divider' }}
        >
          {/* 좌측: 탭 버튼들 / Left: Tab buttons */}
          <Stack direction="row" spacing={1}>
            <Button
              variant="tab"
              className={type === 'text' ? 'Mui-selected' : ''}
              onClick={() => switchType('text')}
              sx={{
                border: '1px solid',
                borderColor: 'grey.400',
              }}
              startIcon={
                <img
                  src={textIcon}
                  alt="Text"
                  style={{
                    width: 20,
                    height: 20,
                    filter:
                      type === 'text'
                        ? 'brightness(0) invert(1)'
                        : 'brightness(0) saturate(100%) invert(29%) sepia(8%) saturate(1088%) hue-rotate(169deg) brightness(93%) contrast(88%)',
                  }}
                />
              }
            >
              텍스트 생성
            </Button>
            <Button
              variant="tab"
              className={type === 'image' ? 'Mui-selected' : ''}
              onClick={() => switchType('image')}
              sx={{
                border: '1px solid',
                borderColor: 'grey.400',
              }}
              startIcon={
                <img
                  src={imageIcon}
                  alt="Image"
                  style={{
                    width: 20,
                    height: 20,
                    filter:
                      type === 'image'
                        ? 'brightness(0) invert(1)'
                        : 'brightness(0) saturate(100%) invert(29%) sepia(8%) saturate(1088%) hue-rotate(169deg) brightness(93%) contrast(88%)',
                  }}
                />
              }
            >
              이미지 생성
            </Button>
          </Stack>

          {/* 우측: KPC 로고 / Right: KPC Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={kpcLogo}
              alt="KPC Logo"
              style={{
                height: '35px',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Stack>

        {/* 공통 캔버스 레이아웃 / Common canvas layout */}
        <Box
          className="canvas-layout"
          sx={{
            mt: 0,
            pb: 0,
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
            background:
              'radial-gradient(60% 140% at 50% -10%, rgba(145,158,171,0.16) 0%, rgba(145,158,171,0.10) 40%, rgba(145,158,171,0.06) 70%, rgba(145,158,171,0.0) 100%)',
            boxShadow:
              '0 6px 24px rgba(145,158,171,0.16), 0 2px 6px rgba(145,158,171,0.12)',
            position: 'relative',
          }}
        >
          {/* 텍스트 생성 탭 - 항상 렌더링하되 display로 제어 */}
          <Box
            sx={{
              display: type === 'text' ? 'block' : 'none',
              width: '100%',
              height: '100%',
            }}
          >
            <TextGenerationTab
              sharedState={sharedState}
              setSharedState={setSharedState}
            />
          </Box>

          {/* 이미지 생성 탭 - 항상 렌더링하되 display로 제어 */}
          <Box
            sx={{
              display: type === 'image' ? 'block' : 'none',
              width: '100%',
              height: '100%',
            }}
          >
            <ImageGenerationTab
              sharedState={sharedState}
              setSharedState={setSharedState}
            />
          </Box>
        </Box>

        {/* 하단 푸터 / Bottom Footer */}
        <Box
          className="footer-container"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            py: 2,
            px: 3,
          }}
        >
          {/* 사용자 ID 및 expKey / User ID and expKey */}
          <Typography variant="caption" className="user-id">
            {(() => {
              const urlParams = new URLSearchParams(window.location.search);
              const expKey = urlParams.get('expKey');
              return expKey 
                ? `Key: ${expKey}, Id: ${userId || '계정연동실패'}`
                : `Id: ${userId || '계정연동실패'}`;
            })()}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ExamPage;
